import React, { useState, useEffect } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Upload,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRight,
  ArrowLeft,
  X,
  Loader2,
  Trash2,
  ExternalLink,
  Ban,
  Sparkles,
  Video,
  MapPin,
  Home,
  Building,
  Navigation,
  Award,
  Zap,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  filesApi,
  doctorsApi,
  type DocumentType,
  type DoctorDocument,
  type DoctorDetail,
  type Specialty,
} from '@/api';
import type { UploadedFile } from '@/api/http-client';
import { AvatarUploader, CoverPhotoUploader } from '@/components/files';
import {
  useDoctorDocuments,
  useUploadDoctorDocument,
  useDeleteDoctorDocument,
} from '@/hooks/doctor/useDoctorDocuments';
import { DoctorProfilePreviewModal } from './DoctorProfilePreviewModal';

export interface DoctorOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  doctor: DoctorDetail | null;
  onRefreshProfile?: () => void;
  initialStep?: number;
}

interface RequiredDocConfig {
  type: DocumentType;
  title: string;
  description: string;
  required: boolean;
  requiredNote?: string;
}

const REQUIRED_DOCS: RequiredDocConfig[] = [
  {
    type: 'CRM',
    title: 'Carteira do CRM (Frente e Verso)',
    description: 'Documento oficial de identidade profissional emitido pelo CRM com foto legível.',
    required: true,
  },
  {
    type: 'DIPLOMA',
    title: 'Diploma de Graduação em Medicina',
    description: 'Frente e verso do diploma autenticado de conclusão de curso reconhecido pelo MEC.',
    required: true,
  },
  {
    type: 'IDENTITY',
    title: 'Documento de Identidade com Foto (RG ou CNH)',
    description: 'Documento oficial com foto válido em todo território nacional.',
    required: true,
  },
];

export function DoctorOnboardingModal({
  open,
  onClose,
  doctor,
  onRefreshProfile,
  initialStep = 1,
}: DoctorOnboardingModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<number>(initialStep);

  useEffect(() => {
    if (initialStep) {
      setCurrentStep(initialStep);
    }
  }, [initialStep, open]);

  // Documents state
  const { data: documents = [], refetch: refetchDocuments } = useDoctorDocuments();
  const uploadMutation = useUploadDoctorDocument();
  const deleteMutation = useDeleteDoctorDocument();
  const [uploadingType, setUploadingType] = useState<DocumentType | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Profile data states (Step 1)
  const [displayName, setDisplayName] = useState(
    (doctor as any)?.displayName ||
    doctor?.profile?.displayName ||
    (doctor as any)?.user?.name ||
    (doctor as any)?.name ||
    ''
  );
  const [tagline, setTagline] = useState(
    (doctor as any)?.tagline ||
    doctor?.profile?.tagline ||
    ''
  );
  const [bio, setBio] = useState(doctor?.bio || (doctor as any)?.profile?.bio || '');
  const [photoUrl, setPhotoUrl] = useState<string | null>(
    (doctor as any)?.photoUrl || doctor?.profile?.photoUrl || null
  );
  const [coverUrl, setCoverUrl] = useState<string | null>(
    (doctor as any)?.coverUrl || (doctor?.profile as any)?.coverUrl || null
  );
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Specialties state (Step 2)
  const { data: allSpecialties = [] } = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: () => doctorsApi.getSpecialties(),
  });
  const [selectedNewSpecialtyId, setSelectedNewSpecialtyId] = useState('');
  const [isAddingSpecialty, setIsAddingSpecialty] = useState(false);

  // Audience & Age limit states (Step 5)
  const [minAge, setMinAge] = useState<number>(doctor?.minAge ?? 0);
  const [maxAge, setMaxAge] = useState<number>(doctor?.maxAge ?? 120);
  const [allowedAgeGroups, setAllowedAgeGroups] = useState<string[]>(doctor?.allowedAgeGroups || ['ADULT']);

  // Modalities & Location states (Step 6)
  const [hasTelemedicine, setHasTelemedicine] = useState(doctor?.hasTelemedicine ?? true);
  const [hasInPerson, setHasInPerson] = useState(doctor?.hasInPerson ?? false);
  const [hasHomeCare, setHasHomeCare] = useState(doctor?.hasHomeCare ?? false);
  const [clinicName, setClinicName] = useState(doctor?.clinicName || '');
  const [clinicCep, setClinicCep] = useState((doctor as any)?.clinicCep || '');
  const [clinicAddress, setClinicAddress] = useState(doctor?.clinicAddress || '');
  const [clinicNumber, setClinicNumber] = useState((doctor as any)?.clinicNumber || '');
  const [clinicComplement, setClinicComplement] = useState((doctor as any)?.clinicComplement || '');
  const [clinicNeighborhood, setClinicNeighborhood] = useState((doctor as any)?.clinicNeighborhood || '');
  const [clinicCity, setClinicCity] = useState(doctor?.clinicCity || '');
  const [clinicState, setClinicState] = useState(doctor?.clinicState || 'SP');
  const [inPersonRadiusKm, setInPersonRadiusKm] = useState<number>((doctor as any)?.inPersonRadiusKm ?? 30);
  const [homeCareRadiusKm, setHomeCareRadiusKm] = useState<number>(doctor?.homeCareRadiusKm ?? 15);
  const [cepLoading, setCepLoading] = useState(false);

  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    const clean = rawVal.replace(/\D/g, '');
    if (clean.length === 8) {
      setCepLoading(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
        const json = await res.json();
        if (!json.erro) {
          if (json.logradouro) setClinicAddress(json.logradouro);
          if (json.bairro) setClinicNeighborhood(json.bairro);
          if (json.localidade) setClinicCity(json.localidade);
          if (json.uf) setClinicState(json.uf);
          if (json.cep) setClinicCep(json.cep);
        }
      } catch (err) {
        console.error('Erro ao consultar CEP:', err);
      } finally {
        setCepLoading(false);
      }
    }
  };

  // Sync initial doctor data when profile finishes loading or updates
  useEffect(() => {
    if (doctor) {
      const incomingDisplayName =
        (doctor as any).displayName ||
        doctor.profile?.displayName ||
        (doctor as any).user?.name ||
        (doctor as any).name;
      const incomingTagline = (doctor as any).tagline ?? doctor.profile?.tagline;
      const incomingBio = doctor.bio ?? (doctor as any).profile?.bio;
      const incomingPhoto = (doctor as any).photoUrl ?? doctor.profile?.photoUrl;
      const incomingCover = (doctor as any).coverUrl ?? (doctor.profile as any)?.coverUrl;

      if (incomingDisplayName !== undefined) setDisplayName(incomingDisplayName || '');
      if (incomingTagline !== undefined) setTagline(incomingTagline || '');
      if (incomingBio !== undefined) setBio(incomingBio || '');
      if (incomingPhoto !== undefined) setPhotoUrl(incomingPhoto || null);
      if (incomingCover !== undefined) setCoverUrl(incomingCover || null);

      if (doctor.minAge !== undefined) setMinAge(doctor.minAge ?? 0);
      if (doctor.maxAge !== undefined) setMaxAge(doctor.maxAge ?? 120);
      if (doctor.allowedAgeGroups) setAllowedAgeGroups(doctor.allowedAgeGroups);
      if (doctor.hasTelemedicine !== undefined) setHasTelemedicine(doctor.hasTelemedicine);
      if (doctor.hasInPerson !== undefined) setHasInPerson(doctor.hasInPerson);
      if (doctor.hasHomeCare !== undefined) setHasHomeCare(doctor.hasHomeCare);
      if (doctor.clinicName) setClinicName(doctor.clinicName);
      if ((doctor as any).clinicCep) setClinicCep((doctor as any).clinicCep);
      if (doctor.clinicAddress) setClinicAddress(doctor.clinicAddress);
      if ((doctor as any).clinicNumber) setClinicNumber((doctor as any).clinicNumber);
      if ((doctor as any).clinicComplement) setClinicComplement((doctor as any).clinicComplement);
      if ((doctor as any).clinicNeighborhood) setClinicNeighborhood((doctor as any).clinicNeighborhood);
      if (doctor.clinicCity) setClinicCity(doctor.clinicCity);
      if (doctor.clinicState) setClinicState(doctor.clinicState);
      if ((doctor as any).inPersonRadiusKm) setInPersonRadiusKm((doctor as any).inPersonRadiusKm);
      if (doctor.homeCareRadiusKm) setHomeCareRadiusKm(doctor.homeCareRadiusKm);
    }
  }, [doctor]);

  const doctorId = doctor?.id || '';
  const doctorStatus = doctor?.status || 'PENDING';
  const isApproved = doctorStatus === 'APPROVED';
  const isRejected = doctorStatus === 'REJECTED';
  const blockedReason = (doctor as any)?.blockedReason || null;

  // Documents checklist
  const crmDoc = documents.find((d) => d.type === 'CRM');
  const diplomaDoc = documents.find((d) => d.type === 'DIPLOMA');
  const identityDoc = documents.find((d) => d.type === 'IDENTITY');

  const essentialUploadedCount = [crmDoc, diplomaDoc, identityDoc].filter(Boolean).length;
  const isAllEssentialUploaded = essentialUploadedCount === 3;

  const handleDocumentUpload = async (type: DocumentType, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O tamanho máximo permitido por documento é de 15MB.',
        variant: 'error',
      });
      return;
    }

    try {
      setUploadingType(type);
      const uploadedFile = await filesApi.uploadFile(
        file,
        doctorId || 'me',
        'DOCTOR',
        'DOCUMENT',
      );

      const targetFileUrl =
        uploadedFile?.key ||
        (uploadedFile as any)?.url ||
        (uploadedFile as any)?.signedUrl ||
        (uploadedFile as any)?.id;

      if (!targetFileUrl) {
        throw new Error('Falha ao processar a identificação do arquivo enviado.');
      }

      await uploadMutation.mutateAsync({
        type,
        fileUrl: targetFileUrl,
      });

      await refetchDocuments();
      toast({
        title: 'Documento enviado!',
        description: 'Documento recebido para auditoria cadastral.',
      });
    } catch (err: any) {
      toast({
        title: 'Erro ao enviar documento',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    } finally {
      setUploadingType(null);
      if (e.target) e.target.value = '';
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      setDeletingId(docId);
      await deleteMutation.mutateAsync(docId);
      toast({ title: 'Documento removido com sucesso.' });
    } catch (err: any) {
      toast({ title: 'Erro ao remover documento', description: err?.message, variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  // Step 1: Save Profile Basic Info
  const handleSaveStep1 = async () => {
    if (!displayName.trim()) {
      toast({ title: 'Preencha seu Nome de Exibição', variant: 'error' });
      return;
    }

    try {
      setIsSavingProfile(true);
      await doctorsApi.updateMyProfile({
        displayName: displayName.trim(),
        tagline: tagline.trim() || undefined,
        bio: bio.trim() || undefined,
        photoUrl: photoUrl || undefined,
        coverUrl: coverUrl || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['doctor'] });
      await onRefreshProfile?.();
      toast({ title: 'Perfil atualizado com sucesso!' });
      setCurrentStep(2);
    } catch (err: any) {
      toast({ title: 'Erro ao salvar perfil', description: err?.message, variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Step 2: Add Specialty
  const handleAddSpecialty = async () => {
    if (!selectedNewSpecialtyId) return;
    try {
      setIsAddingSpecialty(true);
      await doctorsApi.addMySpecialty({ specialtyId: selectedNewSpecialtyId });
      queryClient.invalidateQueries({ queryKey: ['doctor', doctorId, 'specialties'] });
      queryClient.invalidateQueries({ queryKey: ['doctor', 'profile', doctorId] });
      setSelectedNewSpecialtyId('');
      toast({ title: 'Especialidade solicitada! Comprove o RQE para aprovação.' });
    } catch (err: any) {
      toast({ title: 'Erro ao adicionar especialidade', description: err?.message, variant: 'error' });
    } finally {
      setIsAddingSpecialty(false);
    }
  };

  // Step 5 & 6: Save Audience and Modalities (Post-Approval)
  const handleSaveStep5Audience = async () => {
    try {
      setIsSavingProfile(true);
      await doctorsApi.updateMyProfile({
        minAge,
        maxAge,
        allowedAgeGroups,
      });
      queryClient.invalidateQueries({ queryKey: ['doctor', 'profile', doctorId] });
      onRefreshProfile?.();
      toast({ title: 'Público atendido configurado!' });
      setCurrentStep(6);
    } catch (err: any) {
      toast({ title: 'Erro ao salvar faixas etárias', description: err?.message, variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveStep6Modalities = async () => {
    try {
      setIsSavingProfile(true);
      await doctorsApi.updateMyProfile({
        hasTelemedicine,
        hasInPerson,
        hasHomeCare,
        clinicName: clinicName.trim() || undefined,
        clinicCep: clinicCep.trim() || undefined,
        clinicAddress: clinicAddress.trim() || undefined,
        clinicNumber: clinicNumber.trim() || undefined,
        clinicComplement: clinicComplement.trim() || undefined,
        clinicNeighborhood: clinicNeighborhood.trim() || undefined,
        clinicCity: clinicCity.trim() || undefined,
        clinicState: clinicState.trim().toUpperCase() || undefined,
        inPersonRadiusKm,
        homeCareRadiusKm,
      });
      queryClient.invalidateQueries({ queryKey: ['doctor', 'profile', doctorId] });
      onRefreshProfile?.();
      toast({ title: 'Modalidades de atendimento configuradas!' });
      setCurrentStep(7);
    } catch (err: any) {
      toast({ title: 'Erro ao salvar modalidades', description: err?.message, variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const getDocStatusBadge = (doc?: DoctorDocument) => {
    if (!doc) {
      return (
        <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
          Pendente de Envio
        </span>
      );
    }
    if (doc.status === 'APPROVED') {
      return (
        <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Aprovado
        </span>
      );
    }
    if (doc.status === 'REJECTED') {
      return (
        <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20 flex items-center gap-1">
          <Ban className="w-3 h-3" /> Rejeitado
        </span>
      );
    }
    return (
      <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Em Análise
      </span>
    );
  };

  const stepLabels: Record<number, string> = {
    1: 'Perfil & Fotos',
    2: 'Especialidades & RQE',
    3: 'Documentos Obrigatórios',
    4: 'Cadastro em Análise',
    5: 'Público Atendido & Idades',
    6: 'Modalidades & Localização',
    7: 'Tutorial Plantão & Filas',
  };

  const totalSteps = isApproved ? 7 : 4;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-2xl sm:rounded-3xl border border-border shadow-2xl bg-card">
          {/* Header Fixo com Stepper */}
          <div className="border-b border-border bg-card px-5 sm:px-6 py-4 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span>Onboarding &amp; Ativação Médica Medikei</span>
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Complete as etapas para garantir a excelência do seu perfil e habilitar seus atendimentos
                </DialogDescription>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Stepper Progress Bar */}
            <div className="mt-4 pt-3.5 border-t border-border/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-primary flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-extrabold">
                    {currentStep}
                  </span>
                  <span>Etapa {currentStep} de {totalSteps}:</span>{' '}
                  <span className="font-semibold text-foreground">
                    {stepLabels[currentStep]}
                  </span>
                </span>
                <span className="text-[11px] text-muted-foreground font-medium hidden sm:inline-block">
                  {currentStep <= 4
                    ? isAllEssentialUploaded
                      ? '✓ 3 de 3 documentos essenciais enviados'
                      : `${essentialUploadedCount} de 3 documentos essenciais`
                    : 'Fase Pós-Aprovação Liberada'}
                </span>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {[1, 2, 3, 4, 5, 6, 7].slice(0, totalSteps).map((stepNum) => {
                  const isDone = stepNum < currentStep;
                  const isCurrent = stepNum === currentStep;
                  return (
                    <button
                      key={stepNum}
                      type="button"
                      onClick={() => {
                        // Só permite navegar para etapas pós-aprovação se estiver aprovado
                        if (stepNum > 4 && !isApproved) return;
                        setCurrentStep(stepNum);
                      }}
                      className="py-1 text-left cursor-pointer group"
                    >
                      <div
                        className={`h-2 w-full rounded-full transition-all duration-300 ${
                          isDone
                            ? 'bg-primary'
                            : isCurrent
                            ? 'bg-primary ring-2 ring-primary/20 shadow-xs'
                            : 'bg-muted'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Corpo do Modal com Scroll Suave */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-6 text-foreground">
            {/* ETAPA 1: COMPLETAR PERFIL E ADICIONAR FOTOS */}
            {currentStep === 1 && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-base text-foreground">
                      Foto de Perfil, Capa e Apresentação
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Um perfil com fotos profissionais atrai até 4x mais pacientes e agiliza a aprovação.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsPreviewModalOpen(true)}
                    className="rounded-xl text-xs font-bold gap-1.5 border-primary/30 text-primary hover:bg-primary/5 shadow-2xs"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Pré-visualizar Perfil</span>
                  </Button>
                </div>

                {/* Upload e Ajuste de Imagem de Capa Panorâmica (com corte, zoom e rotação) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span>Foto de Capa Panorâmica</span>
                    <span className="text-muted-foreground text-[11px]">Recomendado: 1200x400px (Ajustável)</span>
                  </div>
                  <CoverPhotoUploader
                    currentUrl={coverUrl}
                    onCoverChange={(newUrl) => {
                      const cleanUrl = newUrl || null;
                      setCoverUrl(cleanUrl);
                      doctorsApi.updateMyProfile({ coverUrl: cleanUrl }).catch(() => {});
                      queryClient.invalidateQueries({ queryKey: ['doctor'] });
                      onRefreshProfile?.();
                    }}
                    ownerId={doctorId || 'doctor'}
                  />
                </div>

                {/* Upload e Ajuste de Foto de Perfil (com corte circular, zoom e movimentação) */}
                <div className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border">
                  <AvatarUploader
                    ownerId={doctorId || 'doctor'}
                    ownerType="DOCTOR"
                    currentUrl={photoUrl}
                    onUploadSuccess={(file: UploadedFile) => {
                      const finalKeyOrUrl = file.key || file.url || file.id;
                      setPhotoUrl(finalKeyOrUrl);
                      doctorsApi.updateMyProfile({ photoUrl: finalKeyOrUrl }).catch(() => {});
                      queryClient.invalidateQueries({ queryKey: ['doctor'] });
                      onRefreshProfile?.();
                    }}
                    onDeleteSuccess={() => {
                      setPhotoUrl(null);
                      doctorsApi.updateMyProfile({ photoUrl: null }).catch(() => {});
                      queryClient.invalidateQueries({ queryKey: ['doctor'] });
                      onRefreshProfile?.();
                    }}
                    category="PROFILE_PHOTO"
                    visibility="PUBLIC"
                    size="lg"
                  />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-semibold text-foreground">Foto de Rosto Profissional</p>
                    <p className="text-xs text-muted-foreground">
                      Foto profissional de rosto com ajuste de zoom, enquadramento e corte circular em tempo real.
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Formatos: JPG, PNG, WebP. Máximo 5MB (Recomendado 400x400px).
                    </p>
                  </div>
                </div>

                {/* Campos Textuais */}
                <div className="space-y-3.5 text-xs">
                  <div>
                    <label className="block font-semibold text-foreground mb-1">Nome de Exibição Público</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Ex: Dr. João Silva"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-foreground mb-1">Frase de Destaque / Tagline</label>
                    <input
                      type="text"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      placeholder="Ex: Medicina humanizada, diagnósticos precisos e escuta atenta."
                      className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-foreground mb-1">Biografia e Trajetória Clínica</label>
                    <textarea
                      rows={3}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Conte sobre sua formação, experiência hospitalar, especializações e estilo de atendimento..."
                      className="w-full p-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ETAPA 2: ESPECIALIDADES & RQE */}
            {currentStep === 2 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-bold text-base text-foreground">
                    Especialidades &amp; Comprovação por RQE
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Habilitação médica regulatória conforme diretrizes do CFM
                  </p>
                </div>

                {/* Prerrogativa Generalista */}
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-bold text-sm text-foreground">Clínica Geral (Habilitada por CRM)</span>
                    <Badge variant="outline" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">
                      Ativa
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Todo médico formado com CRM ativo possui habilitação nata para Clínica Geral e Plantão Online 24h na Medikei.
                  </p>
                </div>

                {/* Adicionar Outra Especialidade */}
                <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-3">
                  <span className="font-bold text-xs uppercase tracking-wider text-muted-foreground block">
                    Adicionar Outra Especialidade Médica
                  </span>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <select
                      value={selectedNewSpecialtyId}
                      onChange={(e) => setSelectedNewSpecialtyId(e.target.value)}
                      className="flex-1 px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Selecione uma especialidade...</option>
                      {allSpecialties.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      onClick={handleAddSpecialty}
                      disabled={!selectedNewSpecialtyId || isAddingSpecialty}
                      size="sm"
                      className="rounded-xl text-xs font-bold gap-1 px-4"
                    >
                      {isAddingSpecialty ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
                      <span>Adicionar Especialidade</span>
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Especialidades adicionais ficam com status <em>Aguardando Comprovação</em> até o envio do número ou documento do RQE.
                  </p>
                </div>
              </div>
            )}

            {/* ETAPA 3: DOCUMENTOS COMPROBATÓRIOS */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-base text-foreground">
                      Documentos Obrigatórios de Habilitação
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Envie arquivos em PDF ou imagem legível (máximo 15MB cada)
                    </p>
                  </div>
                  <Badge variant={isAllEssentialUploaded ? 'success' : 'outline'} className="font-bold text-xs">
                    {essentialUploadedCount}/3 Obrigatórios
                  </Badge>
                </div>

                {REQUIRED_DOCS.map((docConfig) => {
                  const uploadedDoc = documents.find((d) => d.type === docConfig.type);
                  const isUploading = uploadingType === docConfig.type;
                  const isDeleting = deletingId === uploadedDoc?.id;
                  const fileUrl = uploadedDoc ? filesApi.getFileUrl(uploadedDoc.fileUrl) : null;

                  return (
                    <div
                      key={docConfig.type}
                      className="p-4 rounded-2xl border border-border bg-card/60 space-y-3 hover:border-primary/30 transition-all shadow-xs"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-foreground text-sm">{docConfig.title}</span>
                            {docConfig.required ? (
                              <Badge variant="error" className="text-[10px] py-0 px-2 font-bold">
                                Obrigatório
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] py-0 px-2 font-medium text-muted-foreground">
                                {docConfig.requiredNote}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">{docConfig.description}</p>
                        </div>
                        <div className="shrink-0">{getDocStatusBadge(uploadedDoc)}</div>
                      </div>

                      <div className="pt-2 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap">
                        {uploadedDoc ? (
                          <div className="flex items-center gap-2 text-xs">
                            {fileUrl && (
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline font-semibold"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                <span>Visualizar anexo</span>
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground italic">Nenhum arquivo enviado</span>
                        )}

                        <div className="flex items-center gap-2 ml-auto">
                          <input
                            type="file"
                            id={`upload-doc-${docConfig.type}`}
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => handleDocumentUpload(docConfig.type, e)}
                            className="hidden"
                          />
                          {uploadedDoc ? (
                            <>
                              <label
                                htmlFor={`upload-doc-${docConfig.type}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-primary/30 text-primary hover:bg-primary/5 transition-colors cursor-pointer ${
                                  isUploading ? 'opacity-60 pointer-events-none' : ''
                                }`}
                              >
                                {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                <span>Substituir</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => handleDeleteDocument(uploadedDoc.id)}
                                disabled={isDeleting}
                                className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors cursor-pointer"
                                title="Remover documento"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <label
                              htmlFor={`upload-doc-${docConfig.type}`}
                              className={`inline-flex items-center justify-center rounded-xl font-bold gap-1.5 text-xs px-4 h-9 cursor-pointer bg-primary text-primary-foreground hover:bg-primary-hover shadow-xs ${
                                isUploading ? 'opacity-60 pointer-events-none' : ''
                              }`}
                            >
                              {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                              <span>Enviar Documento</span>
                            </label>
                          )}
                        </div>
                      </div>

                      {uploadedDoc?.status === 'REJECTED' && uploadedDoc.rejectionReason && (
                        <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <div>
                            <strong>Motivo da recusa pelo administrador:</strong> {uploadedDoc.rejectionReason}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ETAPA 4: CADASTRO EM ANÁLISE / AUDITORIA */}
            {currentStep === 4 && (
              <div className="space-y-6 text-center py-4">
                <div
                  className={`w-18 h-18 rounded-3xl flex items-center justify-center mx-auto shadow-sm ${
                    isApproved
                      ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                      : isRejected
                      ? 'bg-rose-500/10 text-rose-600 border border-rose-500/20'
                      : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                  }`}
                >
                  {isApproved ? (
                    <CheckCircle2 className="w-9 h-9" />
                  ) : isRejected ? (
                    <Ban className="w-9 h-9" />
                  ) : (
                    <Clock className="w-9 h-9 animate-pulse" />
                  )}
                </div>

                <div className="max-w-md mx-auto space-y-2">
                  <h3 className="text-2xl font-black text-foreground tracking-tight">
                    {isApproved
                      ? 'Cadastro Aprovado pela Medikei!'
                      : isRejected
                      ? 'Cadastro com Pendência de Aprovação'
                      : 'Cadastro em Análise Médica'}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {isApproved
                      ? 'Sua documentação e CRM foram auditados e homologados com sucesso! Você já pode concluir a configuração de público atendido e plantão.'
                      : isRejected
                      ? 'O administrador revisou seu cadastro e apontou uma pendência que precisa ser corrigida antes da liberação.'
                      : 'Suas informações e documentos obrigatórios foram recebidos e estão na fila prioritária de análise e homologação da equipe regulatória da Medikei.'}
                  </p>
                </div>

                {/* Exibição do Motivo da Recusa se Rejeitado */}
                {isRejected && blockedReason && (
                  <div className="max-w-md mx-auto p-4 rounded-2xl bg-destructive/10 border border-destructive/20 text-left text-xs text-destructive space-y-1">
                    <span className="font-bold block uppercase tracking-wider text-[10px]">
                      Justificativa informada pelo Administrador:
                    </span>
                    <p className="leading-relaxed font-semibold">{blockedReason}</p>
                  </div>
                )}

                {/* Checklist Resumo */}
                <div className="max-w-md mx-auto p-4 rounded-2xl bg-muted/40 border border-border text-left space-y-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">1. Perfil com Fotos e Bio</span>
                    {photoUrl ? <span className="font-bold text-emerald-600">✓ Concluído</span> : <span className="text-amber-600">Pendente</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">2. Carteira do CRM (Frente/Verso)</span>
                    {crmDoc ? <span className="font-bold text-emerald-600">✓ Enviado</span> : <span className="text-amber-600">Pendente</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">3. Diploma de Medicina</span>
                    {diplomaDoc ? <span className="font-bold text-emerald-600">✓ Enviado</span> : <span className="text-amber-600">Pendente</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">4. Documento com Foto (RG/CNH)</span>
                    {identityDoc ? <span className="font-bold text-emerald-600">✓ Enviado</span> : <span className="text-amber-600">Pendente</span>}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsPreviewModalOpen(true)}
                    className="rounded-xl text-xs font-bold gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    <span>Ver Pré-visualização do Perfil</span>
                  </Button>

                  {(!isApproved || isRejected) && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setCurrentStep(1)}
                      className="rounded-xl text-xs font-bold gap-1.5"
                    >
                      <span>Ajustar Dados e Documentos</span>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* ETAPA 5: PÚBLICO ATENDIDO E FAIXAS ETÁRIAS (PÓS-APROVAÇÃO) */}
            {currentStep === 5 && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-bold text-base text-foreground">
                    Público Atendido &amp; Faixas Etárias
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Defina as idades mínimas e máximas de pacientes que você atende
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block font-semibold text-foreground mb-1">Idade Mínima (anos)</label>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      value={minAge}
                      onChange={(e) => setMinAge(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-input bg-background text-foreground text-sm"
                    />
                    <span className="text-[11px] text-muted-foreground mt-0.5 block">0 para bebês / pediatria livre</span>
                  </div>
                  <div>
                    <label className="block font-semibold text-foreground mb-1">Idade Máxima (anos)</label>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      value={maxAge}
                      onChange={(e) => setMaxAge(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-input bg-background text-foreground text-sm"
                    />
                    <span className="text-[11px] text-muted-foreground mt-0.5 block">120 para livre / adultos</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-xs font-bold text-foreground block">Categorias Clínicas Atendidas:</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {[
                      { key: 'NEWBORN', label: 'Bebês / Lactentes (0 a 2 anos)' },
                      { key: 'CHILD', label: 'Crianças (2 a 11 anos)' },
                      { key: 'ADOLESCENT', label: 'Adolescentes (12 a 17 anos)' },
                      { key: 'ADULT', label: 'Adultos (18 a 59 anos)' },
                      { key: 'ELDERLY', label: 'Idosos (60+ anos)' },
                    ].map((grp) => {
                      const isSelected = allowedAgeGroups.includes(grp.key);
                      return (
                        <button
                          key={grp.key}
                          type="button"
                          onClick={() => {
                            setAllowedAgeGroups((prev) =>
                              isSelected ? prev.filter((k) => k !== grp.key) : [...prev, grp.key]
                            );
                          }}
                          className={`p-3 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-primary/10 border-primary text-primary font-bold'
                              : 'bg-muted/20 border-border text-foreground hover:bg-muted/40'
                          }`}
                        >
                          <span>{grp.label}</span>
                          {isSelected && <Check className="w-4 h-4 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ETAPA 6: MODALIDADES DE ATENDIMENTO & LOCALIZAÇÃO (PÓS-APROVAÇÃO) */}
            {currentStep === 6 && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/80 pb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="text-primary h-5 w-5" />
                    <h3 className="font-bold text-base text-foreground">
                      Modalidades de Atendimento &amp; Localização
                    </h3>
                  </div>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs py-0.5 px-2.5 self-start sm:self-auto">
                    Geolocalização Ativa
                  </Badge>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed">
                  Defina como você atende seus pacientes. Para consultas presenciais e domiciliares, o sistema usará seu endereço e raio de cobertura para exibir seu perfil nas buscas por proximidade e geolocalização.
                </p>

                {/* 3 Cards de Modalidades */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                  {/* Telemedicina */}
                  <div
                    onClick={() => setHasTelemedicine(!hasTelemedicine)}
                    className={`flex flex-col p-4 rounded-2xl border cursor-pointer transition-all ${
                      hasTelemedicine
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-xs'
                        : 'border-border hover:border-muted-foreground/30 bg-card opacity-70'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Video className={`h-5 w-5 ${hasTelemedicine ? 'text-primary' : 'text-muted-foreground'}`} />
                      <input
                        type="checkbox"
                        checked={hasTelemedicine}
                        readOnly
                        className="h-4 w-4 rounded border-input text-primary focus:ring-primary pointer-events-none"
                      />
                    </div>
                    <span className="font-bold text-foreground text-sm">Telemedicina</span>
                    <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Atendimento online por vídeo para todo o Brasil.
                    </span>
                  </div>

                  {/* Presencial (Consultório) */}
                  <div
                    onClick={() => setHasInPerson(!hasInPerson)}
                    className={`flex flex-col p-4 rounded-2xl border cursor-pointer transition-all ${
                      hasInPerson
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-xs'
                        : 'border-border hover:border-muted-foreground/30 bg-card opacity-70'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Building className={`h-5 w-5 ${hasInPerson ? 'text-primary' : 'text-muted-foreground'}`} />
                      <input
                        type="checkbox"
                        checked={hasInPerson}
                        readOnly
                        className="h-4 w-4 rounded border-input text-primary focus:ring-primary pointer-events-none"
                      />
                    </div>
                    <span className="font-bold text-foreground text-sm">Presencial (Consultório)</span>
                    <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      O paciente vai até sua clínica ou consultório físico.
                    </span>
                  </div>

                  {/* Atendimento Domiciliar */}
                  <div
                    onClick={() => setHasHomeCare(!hasHomeCare)}
                    className={`flex flex-col p-4 rounded-2xl border cursor-pointer transition-all ${
                      hasHomeCare
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-xs'
                        : 'border-border hover:border-muted-foreground/30 bg-card opacity-70'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Home className={`h-5 w-5 ${hasHomeCare ? 'text-primary' : 'text-muted-foreground'}`} />
                      <input
                        type="checkbox"
                        checked={hasHomeCare}
                        readOnly
                        className="h-4 w-4 rounded border-input text-primary focus:ring-primary pointer-events-none"
                      />
                    </div>
                    <span className="font-bold text-foreground text-sm">Atendimento Domiciliar</span>
                    <span className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Você vai até a residência do paciente dentro do seu raio.
                    </span>
                  </div>
                </div>

                {/* Endereço Base / Consultório (Exibido se Presencial ou Domiciliar selecionados) */}
                {(hasInPerson || hasHomeCare) && (
                  <div className="pt-4 border-t border-border space-y-4">
                    <h4 className="font-semibold text-sm text-foreground flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-primary" />
                      <span>Endereço Base / Consultório</span>
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2 space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Nome do Local / Clínica (Opcional)</label>
                        <input
                          type="text"
                          placeholder="Ex: Consultório Dr. Silva, Edifício Medical Center"
                          value={clinicName}
                          onChange={(e) => setClinicName(e.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
                          <span>CEP</span>
                          {cepLoading && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                        </label>
                        <input
                          type="text"
                          placeholder="00000-000"
                          maxLength={9}
                          value={clinicCep}
                          onChange={(e) => setClinicCep(e.target.value)}
                          onBlur={handleCepBlur}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="md:col-span-3 space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Logradouro / Rua</label>
                        <input
                          type="text"
                          placeholder="Av. Paulista, Rua das Flores..."
                          value={clinicAddress}
                          onChange={(e) => setClinicAddress(e.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Número</label>
                        <input
                          type="text"
                          placeholder="123"
                          value={clinicNumber}
                          onChange={(e) => setClinicNumber(e.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Complemento / Sala</label>
                        <input
                          type="text"
                          placeholder="Sala 402, Bloco B"
                          value={clinicComplement}
                          onChange={(e) => setClinicComplement(e.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted-foreground">Bairro</label>
                        <input
                          type="text"
                          placeholder="Bela Vista"
                          value={clinicNeighborhood}
                          onChange={(e) => setClinicNeighborhood(e.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2 space-y-1">
                          <label className="text-xs font-semibold text-muted-foreground">Cidade</label>
                          <input
                            type="text"
                            placeholder="São Paulo"
                            value={clinicCity}
                            onChange={(e) => setClinicCity(e.target.value)}
                            className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-muted-foreground">UF</label>
                          <input
                            type="text"
                            placeholder="SP"
                            maxLength={2}
                            value={clinicState}
                            onChange={(e) => setClinicState(e.target.value.toUpperCase())}
                            className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary uppercase text-center font-bold text-foreground"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sliders de Abrangência (Raio em KM) */}
                    <div className="pt-3 border-t border-border/60 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {hasInPerson && (
                        <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-xs text-foreground flex items-center gap-1.5">
                              <Building className="h-4 w-4 text-primary" />
                              <span>Abrangência Presencial</span>
                            </span>
                            <span className="inline-flex items-center rounded-full border bg-card text-xs px-2.5 py-1 font-bold text-primary border-primary/30">
                              {inPersonRadiusKm} km
                            </span>
                          </div>
                          <input
                            type="range"
                            min="5"
                            max="100"
                            step="5"
                            value={inPersonRadiusKm}
                            onChange={(e) => setInPersonRadiusKm(Number(e.target.value))}
                            className="w-full cursor-pointer accent-primary"
                          />
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Seu consultório será recomendado prioritariamente a pacientes situados em até <strong>{inPersonRadiusKm} km</strong>.
                          </p>
                        </div>
                      )}

                      {hasHomeCare && (
                        <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-xs text-foreground flex items-center gap-1.5">
                              <Home className="h-4 w-4 text-emerald-600" />
                              <span>Raio para Visitas Domiciliares</span>
                            </span>
                            <span className="inline-flex items-center rounded-full border bg-card text-xs px-2.5 py-1 font-bold text-emerald-600 border-emerald-500/30">
                              {homeCareRadiusKm} km
                            </span>
                          </div>
                          <input
                            type="range"
                            min="5"
                            max="100"
                            step="5"
                            value={homeCareRadiusKm}
                            onChange={(e) => setHomeCareRadiusKm(Number(e.target.value))}
                            className="w-full cursor-pointer accent-emerald-600"
                          />
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Você atenderá pacientes em residências num raio máximo de deslocamento de <strong>{homeCareRadiusKm} km</strong>.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ETAPA 7: TUTORIAL DO PLANTÃO ONLINE & FILAS EXPRESSAS */}
            {currentStep === 7 && (
              <div className="space-y-6 text-center py-2">
                <div className="w-18 h-18 rounded-3xl bg-blue-600/10 text-blue-600 border border-blue-600/20 flex items-center justify-center mx-auto shadow-sm">
                  <Zap className="w-9 h-9 animate-pulse" />
                </div>

                <div className="max-w-md mx-auto space-y-2">
                  <h3 className="text-2xl font-black text-foreground tracking-tight">
                    Como Funciona o Plantão Online 24h
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Você pode atender pacientes sob demanda no pronto atendimento express sempre que tiver disponibilidade.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left text-xs">
                  <div className="p-4 rounded-2xl bg-muted/40 border border-border space-y-2">
                    <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-black">
                      1
                    </div>
                    <span className="font-bold block text-foreground">Ligue o Switch "Online"</span>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Na barra superior do portal, clique no botão para ficar "Plantonista Online". O sistema incluirá você na distribuição imediata.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-muted/40 border border-border space-y-2">
                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-black">
                      2
                    </div>
                    <span className="font-bold block text-foreground">Receba Chamadas com Alarme</span>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Ao surgir um paciente na fila, um alarme sonoro toca e o modal de atendimento exibe os sintomas com 30 segundos para aceite.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-muted/40 border border-border space-y-2">
                    <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-black">
                      3
                    </div>
                    <span className="font-bold block text-foreground">Atenda e Prescreva</span>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      A sala de vídeo abre direto no navegador com prontuário SOAP, receitas integradas MeMed e repasse direto na sua carteira.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Rodapé Fixo */}
          <div className="p-4 sm:px-6 border-t border-border bg-card/95 backdrop-blur-sm shrink-0 flex items-center justify-between gap-3">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                type="button"
                onClick={() => setCurrentStep((prev) => (prev > 1 ? prev - 1 : 1))}
                className="rounded-xl font-semibold gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                className="rounded-xl font-medium"
              >
                Fechar
              </Button>
            )}

            {currentStep === 1 && (
              <Button
                type="button"
                onClick={handleSaveStep1}
                disabled={isSavingProfile}
                className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20"
              >
                {isSavingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span>Salvar e Avançar para Especialidades</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 2 && (
              <Button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20"
              >
                <span>Avançar para Documentos</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 3 && (
              <Button
                type="button"
                onClick={() => setCurrentStep(4)}
                className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20"
              >
                <span>Submeter e Ver Status da Auditoria</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 4 && (
              isApproved ? (
                <Button
                  type="button"
                  onClick={() => setCurrentStep(5)}
                  className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <span>Continuar Onboarding (Faixas &amp; Modalidades)</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl font-bold px-7 shadow-md shadow-primary/20"
                >
                  Fechar
                </Button>
              )
            )}

            {currentStep === 5 && (
              <Button
                type="button"
                onClick={handleSaveStep5Audience}
                disabled={isSavingProfile}
                className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20"
              >
                {isSavingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span>Salvar e Avançar para Modalidades</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 6 && (
              <Button
                type="button"
                onClick={handleSaveStep6Modalities}
                disabled={isSavingProfile}
                className="rounded-xl font-bold gap-1.5 px-6 shadow-md shadow-primary/20"
              >
                {isSavingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span>Salvar e Ver Tutorial do Plantão</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            {currentStep === 7 && (
              <Button
                type="button"
                onClick={onClose}
                className="rounded-xl font-bold px-8 shadow-md shadow-primary/20 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <span>Concluir Tutorial e Ir para o Painel</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pré-visualização do Perfil Público */}
      <DoctorProfilePreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        data={{
          displayName,
          name: (doctor as any)?.name,
          crm: doctor?.crm,
          crmState: doctor?.crmState,
          tagline,
          bio,
          photoUrl,
          coverUrl,
          specialty: doctor?.primarySpecialty?.name || 'Clínica Geral',
          specialties: (doctor as any)?.specialties || [],
          languages: doctor?.profile?.languages || ['Português'],
          awards: (doctor?.profile as any)?.awards || [],
          publications: (doctor?.profile as any)?.publications,
          minAge,
          maxAge,
          allowedAgeGroups,
          hasTelemedicine,
          hasInPerson,
          hasHomeCare,
          clinicName,
          clinicAddress,
          clinicCity,
          clinicState,
          homeCareRadiusKm,
        }}
      />
    </>
  );
}
